import { useState } from "react";
import { Seo } from "../components/Seo";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Phone,
  VideoCamera,
  InstagramLogo,
  Sparkle,
  Timer,
  Star,
  Quotes,
  MapTrifold,
  PiggyBank,
  Compass,
  Ticket,
  ShieldCheck,
} from "@phosphor-icons/react";

const PROFILE_IMG =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_journey-craft-71/artifacts/z8jwv1wf_Insta_Saver_%40wanderfullyflying_dp_HD.jpg";
const INSTAGRAM_URL = "https://instagram.com/wanderfullyflying";

const DESTINATIONS = [
  "Vietnam",
  "Phu Quoc",
  "Thailand",
  "Japan",
  "Oman",
];

const TESTIMONIALS = [
  {
    name: "Ritika M.",
    handle: "ritikawanders",
    trip: "Vietnam · 9 days",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop&crop=faces",
    text: "Kayzal & Sachin literally saved my Hanoi → Ha Giang loop. They flagged which sleeper bus to avoid, the exact homestay in Dong Van, and helped me swap out a tourist trap I had bookmarked. Best ₹4,900 I have spent on a trip.",
    rating: 5,
  },
  {
    name: "Aditya S.",
    handle: "adi.on.the.move",
    trip: "Japan · 12 days",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=faces",
    text: "Was overwhelmed by JR Pass vs IC card math. Sachin broke it down in 5 minutes. Their Osaka food list alone was worth the call — every place they sent me to was a hit. No AI itinerary comes close.",
    rating: 5,
  },
  {
    name: "Priya & Kunal",
    handle: "thebrokehoneymoon",
    trip: "Phu Quoc · 6 days",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=faces",
    text: "Booked the video call two weeks before our honeymoon. Kayzal shared the exact resort she would go back to, the private beach we would never have found, and which sunset spot to skip. It felt like advice from a best friend, not a travel agent.",
    rating: 5,
  },
  {
    name: "Rohan D.",
    handle: "rohan.roams",
    trip: "Thailand · 14 days",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=faces",
    text: "I had a rough itinerary but zero clue how to split time between Chiang Mai, Pai and the islands. Sachin re-shuffled the whole thing in 20 mins based on my dates and budget. Saved me at least ₹30k in booking mistakes.",
    rating: 5,
  },
  {
    name: "Meera K.",
    handle: "meerawrites",
    trip: "Oman · 7 days",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=faces",
    text: "Oman is niche — most Instagram creators haven't even been. Kayzal actually had, and every rec she gave landed. Genuine, warm, no upsell energy.",
    rating: 5,
  },
];

const PAYMENT_PAGES = {
  audio: "https://rzp.io/rzp/38cAm5FR",
  video: "https://rzp.io/rzp/oFVwFbF7",
};

export default function Consultation() {
  const [tier, setTier] = useState("video");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    trip_destination: "Vietnam",
    query: "",
    preferred_slot: "",
  });
  const [loading, setLoading] = useState(false);

  const tiers = {
    audio: { label: "Audio Call", price: 2900, icon: Phone },
    video: { label: "Video Call", price: 4900, icon: VideoCamera },
  };

  const handlePay = async (e) => {
    e.preventDefault();
    const { name, email, phone, trip_destination } = form;
    if (!name.trim() || !email.trim() || !phone.trim() || !trip_destination) {
      toast.error("Please fill your name, email, phone and trip.");
      return;
    }
    if (!/^\+?\d[\d\s-]{7,}$/.test(phone.trim())) {
      toast.error("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    try {
      // Save the booking as "pending" so we have the customer details even
      // if they never complete payment on Razorpay's hosted page.
      const { data } = await api.post("/consultation/create-booking", {
        call_type: tier,
        ...form,
      });

      // Redirect the same tab straight to Razorpay Payment Page with prefill.
      const url = new URL(PAYMENT_PAGES[tier]);
      url.searchParams.set("prefill[name]", form.name);
      url.searchParams.set("prefill[email]", form.email);
      url.searchParams.set("prefill[contact]", form.phone);
      url.searchParams.set("notes[booking_id]", data.booking_id);
      url.searchParams.set("notes[trip]", form.trip_destination);
      window.location.href = url.toString();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="1:1 Travel Consultation with @wanderfullyflying"
        description="Book a personal 20-minute call with Kayzal & Sachin to plan your next trip. Audio ₹2900 · Video ₹4900."
        canonical="https://flycher.com/consultation"
      />
      <div className="min-h-screen bg-white text-neutral-900">
        {/* IG-style top bar */}
        <header
          className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-neutral-200"
          data-testid="consultation-header"
        >
          <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <InstagramLogo size={22} weight="bold" />
              <span
                className="font-semibold tracking-tight"
                style={{ fontFamily: "'Grand Hotel', cursive", fontSize: 22 }}
              >
                wanderfullyflying
              </span>
            </div>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="text-sm font-semibold text-[#0095F6] hover:text-[#1877F2]"
              data-testid="ig-link"
            >
              View profile
            </a>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 pb-24">
          {/* Profile section */}
          <section
            className="pt-6 pb-6 flex items-start gap-5 sm:gap-10"
            data-testid="profile-section"
          >
            <div className="shrink-0">
              <div
                className="rounded-full p-[3px]"
                style={{
                  background:
                    "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)",
                }}
              >
                <img
                  src={PROFILE_IMG}
                  alt="Kayzal & Sachin"
                  className="w-24 h-24 sm:w-36 sm:h-36 rounded-full object-cover border-4 border-white"
                  data-testid="profile-dp"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="text-lg sm:text-xl font-light">
                  wanderfullyflying
                </div>
                <button
                  type="button"
                  onClick={() => {
                    document
                      .querySelector('[data-testid="booking-section"]')
                      ?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="text-sm font-semibold text-white bg-[#0095F6] hover:bg-[#1877F2] px-4 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                  data-testid="consult-now-btn"
                >
                  📞 Consult Now
                </button>
              </div>
              <div className="flex gap-6 mt-4 text-sm">
                <div>
                  <span className="font-semibold">14</span>{" "}
                  <span className="text-neutral-500">posts</span>
                </div>
                <div>
                  <span className="font-semibold">156.7K</span>{" "}
                  <span className="text-neutral-500">followers</span>
                </div>
                <div>
                  <span className="font-semibold">66</span>{" "}
                  <span className="text-neutral-500">following</span>
                </div>
              </div>
              <div className="mt-4 text-sm leading-relaxed">
                <div className="font-semibold">
                  KAYZAL &amp; SACHIN | Lifestyle &amp; Travel
                </div>
                <div>🌍 Sharing worldly experiences through storytelling</div>
                <div>✈️ Quit 9-5 to chase our travel love</div>
                <div>🏆 Top 100 Travel Creators</div>
                <div className="text-[#00376B]">📧 wanderfullyflying@gmail.com</div>
              </div>
            </div>
          </section>

          {/* IG-style highlights row (services) */}
          <section className="border-t border-neutral-200 pt-6" data-testid="highlights">
            <div className="flex gap-6 overflow-x-auto pb-2 no-scrollbar">
              {[
                { label: "1:1 Calls", emoji: "📞" },
                { label: "Trip Plans", emoji: "🗺️" },
                { label: "Vietnam", emoji: "🇻🇳" },
                { label: "Thailand", emoji: "🇹🇭" },
                { label: "Japan", emoji: "🇯🇵" },
                { label: "Oman", emoji: "🇴🇲" },
                { label: "Phu Quoc", emoji: "🏝️" },
              ].map((h) => (
                <div
                  key={h.label}
                  className="flex flex-col items-center gap-1 shrink-0"
                >
                  <div className="w-16 h-16 rounded-full border border-neutral-300 flex items-center justify-center text-2xl bg-gradient-to-br from-neutral-50 to-neutral-100">
                    {h.emoji}
                  </div>
                  <span className="text-xs">{h.label}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Book a call */}
          <section
            className="mt-8 border-t border-neutral-200 pt-8"
            data-testid="booking-section"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkle size={20} className="text-[#DD2A7B]" weight="fill" />
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                Book a 1:1 Travel Consultation
              </h1>
            </div>
            <p className="text-sm text-neutral-600 mb-6">
              A private 20-minute call with Kayzal &amp; Sachin — real, unfiltered
              advice on your upcoming trip. Itinerary tweaks, hidden gems, budget
              reality checks, and honest recommendations.
            </p>

            {/* Benefits grid — the actual funnel copy */}
            <div className="mb-8" data-testid="benefits-grid">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-3 font-semibold">
                Here&apos;s what your 20 minutes buys you
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  {
                    icon: MapTrifold,
                    title: "Itinerary tweaks, day-by-day",
                    text: "We'll rework your plan on the call — kill the tourist traps, add the local gems, fix the pacing.",
                  },
                  {
                    icon: PiggyBank,
                    title: "Budget hacks that actually work",
                    text: "Where to splurge, where to save. Most travelers walk away saving ₹15–30k on a 10-day trip.",
                  },
                  {
                    icon: Compass,
                    title: "Hidden gems only we know",
                    text: "The homestay, the sunset point, the ₹200 street food joint — the stuff Google & AI can't find.",
                  },
                  {
                    icon: Ticket,
                    title: "Booking cheat sheet",
                    text: "Which platform gives best rates on flights, stays, activities — and when to book each for max savings.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Visa, forex & scam alerts",
                    text: "E-visa gotchas, best forex cards for your route, the exact scams to sidestep at each destination.",
                  },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <div
                      key={b.title}
                      className="flex items-start gap-3 p-4 rounded-2xl border border-neutral-200 bg-white hover:border-[#DD2A7B]/40 hover:shadow-sm transition-all"
                    >
                      <div
                        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-white"
                        style={{
                          background:
                            "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)",
                        }}
                      >
                        <Icon size={20} weight="fill" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm mb-0.5">
                          {b.title}
                        </div>
                        <p className="text-xs text-neutral-600 leading-relaxed">
                          {b.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 flex items-center justify-center gap-2 text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-full px-4 py-2 w-fit mx-auto">
                <Sparkle size={13} weight="fill" className="text-[#DD2A7B]" />
                <span>
                  200+ travelers · <b className="text-neutral-800">4.9/5</b> · avg
                  saving ₹22,000 per trip
                </span>
              </div>
            </div>

            {/* Tier selector */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {Object.entries(tiers).map(([key, t]) => {
                const Icon = t.icon;
                const active = tier === key;
                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setTier(key)}
                    data-testid={`tier-${key}`}
                    className={`text-left rounded-2xl p-5 border-2 transition-all ${
                      active
                        ? "border-[#DD2A7B] bg-gradient-to-br from-pink-50 to-orange-50 shadow-md"
                        : "border-neutral-200 hover:border-neutral-400 bg-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                          active
                            ? "bg-gradient-to-br from-[#F58529] to-[#DD2A7B] text-white"
                            : "bg-neutral-100 text-neutral-700"
                        }`}
                      >
                        <Icon size={22} weight="fill" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-base">{t.label}</div>
                        <div className="text-xs text-neutral-500 flex items-center gap-1">
                          <Timer size={12} weight="bold" /> 20 minutes
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold">
                          ₹{t.price.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Form */}
            <form onSubmit={handlePay} className="space-y-4" data-testid="booking-form">
              <Field
                label="Your name"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Priya Sharma"
                testid="input-name"
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  placeholder="you@email.com"
                  testid="input-email"
                  required
                />
                <Field
                  label="Phone / WhatsApp"
                  type="tel"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  placeholder="+91 98xxxxxxxx"
                  testid="input-phone"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-neutral-600 mb-1.5">
                  Upcoming trip
                </label>
                <select
                  value={form.trip_destination}
                  onChange={(e) =>
                    setForm({ ...form, trip_destination: e.target.value })
                  }
                  data-testid="input-trip"
                  className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#DD2A7B]/40 focus:border-[#DD2A7B]"
                >
                  {DESTINATIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>

              <Field
                label="Preferred date & time (optional)"
                value={form.preferred_slot}
                onChange={(v) => setForm({ ...form, preferred_slot: v })}
                placeholder="e.g. Sat 22nd, 7pm IST"
                testid="input-slot"
              />

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-neutral-600 mb-1.5">
                  What do you want to discuss? (optional)
                </label>
                <textarea
                  rows={3}
                  value={form.query}
                  onChange={(e) => setForm({ ...form, query: e.target.value })}
                  data-testid="input-query"
                  placeholder="Rough dates, budget, must-do experiences, whether solo/couple/family…"
                  className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#DD2A7B]/40 focus:border-[#DD2A7B] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="pay-btn"
                className="w-full rounded-xl py-3.5 font-semibold text-white text-base shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.99]"
                style={{
                  background:
                    "linear-gradient(45deg,#F58529,#DD2A7B,#8134AF,#515BD4)",
                }}
              >
                {loading
                  ? "Opening payment…"
                  : `Continue to Pay ₹${tiers[tier].price.toLocaleString("en-IN")}`}
              </button>

              <p className="text-xs text-center text-neutral-500 mt-2">
                Secure payment via Razorpay · Payment opens in a new tab.
              </p>
            </form>
          </section>

          {/* Testimonials */}
          <section
            className="mt-10 border-t border-neutral-200 pt-8"
            data-testid="testimonials-section"
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="flex items-center">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    size={18}
                    weight="fill"
                    className="text-[#F5A623]"
                  />
                ))}
              </div>
              <span className="text-sm font-semibold ml-1">4.9 · 200+ calls</span>
            </div>
            <h2 className="text-lg font-semibold mb-5">
              What travelers say after their call
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TESTIMONIALS.map((t) => (
                <article
                  key={t.name}
                  data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                  className="relative bg-white border border-neutral-200 rounded-2xl p-5 hover:shadow-md hover:border-neutral-300 transition-all"
                >
                  <Quotes
                    size={28}
                    weight="fill"
                    className="absolute top-4 right-4 text-neutral-100"
                  />
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-11 h-11 rounded-full object-cover ring-2 ring-white shadow-sm"
                      loading="lazy"
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">
                        {t.name}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star
                        key={i}
                        size={13}
                        weight="fill"
                        className="text-[#F5A623]"
                      />
                    ))}
                  </div>
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    {t.text}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-neutral-500 bg-neutral-50 px-2.5 py-1 rounded-full">
                    ✈️ {t.trip}
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-neutral-600 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl border border-neutral-300 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#DD2A7B]/40 focus:border-[#DD2A7B]"
      />
    </div>
  );
}
